import { ApolloError, FetchResult, useMutation } from "@apollo/client";

import {
  DASHBOARDS,
  REMOVE_WORKSPACE_DASHBOARD,
  WORKSPACE,
  WORKSPACES,
  WorkspaceClientName
} from "api/workspace";

import { Workspace } from "models/workspace";

import { useWorkspaceContext } from "components/workspace/hooks/useWorkspaceContext";
import { useWorkspaceDispatch } from "components/workspace/hooks/useWorkspaceDispatch";

import { switchActiveDashboardIfNeeded } from "../utils/switchActiveDashboardIfNeeded";

interface UseHideDashboardOptions {
  onCompleted?: () => void;
  onError?: (error: ApolloError) => void;
}

/**
 * Function to hide/remove a dashboard from a workspace
 * @param dashboardId - ID of the dashboard to hide
 * @param workspace - Target workspace to remove the dashboard from
 */
type HideDashboardMutate = (
  dashboardId: string,
  workspace: Workspace
) => Promise<FetchResult>;

export type UseHideDashboardReturnT = {
  mutate: HideDashboardMutate;
  isLoading: boolean;
};

/**
 * Custom hook to hide/remove a dashboard from a workspace.
 */
export function useHideDashboard({
  onCompleted,
  onError
}: UseHideDashboardOptions = {}): UseHideDashboardReturnT {
  const { workspace: currentWorkspace, activeDashboardId } = useWorkspaceContext();
  const workspaceDispatch = useWorkspaceDispatch();
  const [hideDashboard, { loading }] = useMutation(REMOVE_WORKSPACE_DASHBOARD);

  const mutate = async (dashboardId: string, workspace: Workspace) => {
    return hideDashboard({
      variables: {
        workspaceId: workspace.workspaceId,
        dashboardId
      },
      context: {
        clientName: WorkspaceClientName
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        { query: WORKSPACES, context: { clientName: WorkspaceClientName } },
        {
          query: WORKSPACE,
          variables: { id: workspace.workspaceId },
          context: { clientName: WorkspaceClientName }
        },
        { query: DASHBOARDS, context: { clientName: WorkspaceClientName } }
      ],
      onCompleted: () => {
        // Check if the removed dashboard is the currently selected one
        // Only switch if we're operating on the current workspace context
        if (currentWorkspace && workspace.workspaceId === currentWorkspace.workspaceId) {
          switchActiveDashboardIfNeeded(
            workspace,
            activeDashboardId,
            dashboardId,
            workspaceDispatch
          );
        }
        onCompleted?.();
      },
      onError: (error: ApolloError) => {
        // eslint-disable-next-line no-console
        console.error(error.message, error);
        onError?.(error);
      }
    });
  };

  return { mutate, isLoading: loading };
}
